import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { financialAPI, companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const CURRENT_YEAR = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - i);
const TABS  = ['Income Statement', 'Balance Sheet', 'Cash Flow'];

// ── CSV helpers ───────────────────────────────────────────────────────────────
function toCSV(headers, rows) {
  const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
  return [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
}
function downloadCSV(filename, csv) {
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ── Shared UI ─────────────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '1.1rem' }}>
      <div style={{
        fontSize: '0.7rem', fontWeight: 700, color: 'var(--color-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em',
        marginBottom: '0.4rem', paddingBottom: '0.3rem',
        borderBottom: '1px solid var(--color-border)',
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function Row({ label, value, bold, color, indent, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '0.42rem 0', borderBottom: '1px solid rgba(255,255,255,0.04)',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span style={{
        fontSize: '0.87rem',
        paddingLeft: indent ? '1rem' : 0,
        color: bold ? 'inherit' : 'var(--color-muted)',
        fontWeight: bold ? 600 : 400,
      }}>
        {label}
        {onClick && <span style={{ fontSize: '0.7rem', color: 'var(--color-primary)', marginLeft: '0.4rem' }}>›</span>}
      </span>
      <span style={{ fontSize: '0.87rem', fontWeight: bold ? 700 : 400, color: color || 'inherit', flexShrink: 0, marginLeft: '1rem' }}>
        {value}
      </span>
    </div>
  );
}

function Divider() {
  return <div style={{ height: '1px', background: 'var(--color-border)', margin: '0.3rem 0' }} />;
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function Financials() {
  const { user, business, parentCompany, isOwner, isEnterprise } = useAuth();
  const navigate     = useNavigate();
  const isManager    = user?.role === 'manager';
  const canViewFull  = isOwner || isManager;

  const [tab,        setTab]        = useState(TABS[0]);
  const [year,       setYear]       = useState('');
  const [month,      setMonth]      = useState('');
  const [summary,    setSummary]    = useState(null);
  const [stmts,      setStmts]      = useState(null);
  const [loadingIS,  setLoadingIS]  = useState(true);
  const [loadingST,  setLoadingST]  = useState(true);
  const [businesses, setBusinesses] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [exporting,  setExporting]  = useState(false);

  // Load business list for owners (Enterprise — multi-business switcher)
  useEffect(() => {
    if (isOwner && isEnterprise) {
      companyAPI.listBiz()
        .then(r => {
          const list = r.data.businesses || [];
          setBusinesses(list);
          if (!selectedId && business?.id) setSelectedId(business.id);
        })
        .catch(() => {});
    }
    // Managers always see their own business only
    if (isManager && business?.id) setSelectedId(business.id);
  }, [isOwner, isManager, isEnterprise, business?.id]); // eslint-disable-line

  // Which business's data to show
  const bizId = isOwner ? (selectedId || business?.id) : business?.id;

  // Current currency — prefer selected biz currency for owners
  const selectedBiz = businesses.find(b => b.id === bizId);
  const cur = selectedBiz?.currency || parentCompany?.currency || business?.currency || '';

  const fmt = n =>
    `${cur} ${new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0)}`;
  const valColor = n => Number(n) < 0 ? 'var(--color-danger)' : Number(n) > 0 ? 'var(--color-success)' : 'inherit';

  const goToDetail = (type) => {
    const qs = new URLSearchParams();
    if (year)  qs.set('year', year);
    if (month) qs.set('month', month);
    navigate(`/financials/detail/${type}${qs.toString() ? '?' + qs.toString() : ''}`);
  };

  const periodLabel = () => {
    if (!year)  return 'All time';
    if (month)  return `${MONTHS[parseInt(month) - 1]} ${year}`;
    return String(year);
  };

  // ── Load IS summary ───────────────────────────────────────────────────────
  const loadIS = useCallback(() => {
    if (!canViewFull || !bizId) return;
    setLoadingIS(true);
    const params = {};
    if (year)  params.year  = year;
    if (month) params.month = month;

    const req = isOwner
      ? companyAPI.getBizSummary(bizId, params).then(r => setSummary(r.data.summary))
      : financialAPI.summary(params).then(r => setSummary(r.data.summary));
    req.catch(() => {}).finally(() => setLoadingIS(false));
  }, [year, month, bizId, isOwner, canViewFull]);

  // ── Load BS + CF statements ───────────────────────────────────────────────
  const loadStmts = useCallback(() => {
    if (!canViewFull || !bizId) return;
    setLoadingST(true);
    const req = isOwner
      ? companyAPI.getBizStatements(bizId).then(r => setStmts(r.data))
      : financialAPI.statements().then(r => setStmts(r.data));
    req.catch(() => {}).finally(() => setLoadingST(false));
  }, [bizId, isOwner, canViewFull]);

  useEffect(() => { loadIS(); }, [loadIS]);
  useEffect(() => { loadStmts(); }, [loadStmts]);

  // ── Export ────────────────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!stmts || !summary) return;
    setExporting(true);
    try {
      const bizName = selectedBiz?.name || business?.name || 'business';
      const date    = new Date().toISOString().slice(0, 10);
      const IS = stmts.income_statement;
      const BS = stmts.balance_sheet;
      const CF = stmts.cash_flow;

      const rows = [
        ['INCOME STATEMENT', '', ''],
        ['Revenue',          Number(IS.revenue).toFixed(2),      cur],
        ['COGS',             Number(IS.cogs).toFixed(2),         cur],
        ['Gross Profit',     Number(IS.gross_profit).toFixed(2), cur],
        ['Expenses',         Number(IS.expenses).toFixed(2),     cur],
        ['Stock adjustments',Number(IS.stock_adjustments || 0).toFixed(2), cur],
        ['Net Profit',       Number(IS.net_profit).toFixed(2),   cur],
        ['', '', ''],
        ['BALANCE SHEET', '', ''],
        ['Cash',             Number(BS.assets.current.cash).toFixed(2),       cur],
        ['Receivables',      Number(BS.assets.current.receivables).toFixed(2),cur],
        ['Stock Value',      Number(BS.assets.current.stock_value).toFixed(2),cur],
        ['Total Current',    Number(BS.assets.current.total).toFixed(2),      cur],
        ['Total Fixed',      Number(BS.assets.fixed.total).toFixed(2),        cur],
        ['Total Assets',     Number(BS.assets.total).toFixed(2),              cur],
        ['Customer credit (liability)', Number(BS.liabilities.total || 0).toFixed(2), cur],
        ['Owner Capital',    Number(BS.equity.owner_capital).toFixed(2),      cur],
        ['Retained Earnings',Number(BS.equity.retained_earnings).toFixed(2),  cur],
        ['Total Equity',     Number(BS.equity.total).toFixed(2),              cur],
        ['', '', ''],
        ['CASH FLOW', '', ''],
        ['Cash from Customers',      Number(CF.operating.cash_from_customers).toFixed(2),   cur],
        ['Payments to Suppliers',    Number(CF.operating.payments_to_suppliers).toFixed(2), cur],
        ['Payments for Expenses',    Number(CF.operating.payments_for_expenses).toFixed(2), cur],
        ['Net from Operations',      Number(CF.operating.net).toFixed(2),                   cur],
        ['Net from Investing',       Number(CF.investing.net).toFixed(2),                   cur],
        ['Net from Financing',       Number(CF.financing.net).toFixed(2),                   cur],
        ['Net Change in Cash',       Number(CF.net_change).toFixed(2),                      cur],
      ];

      downloadCSV(
        `${bizName.replace(/\s+/g, '-').toLowerCase()}-financials-${date}.csv`,
        toCSV(['Line Item', 'Amount', 'Currency'], rows)
      );
    } finally { setExporting(false); }
  };

  // ── Limited staff view ────────────────────────────────────────────────────
  if (!canViewFull) return (
    <div className="page">
      <BackButton />
      <h1 className="page-title">Financials</h1>
      <p className="text-muted" style={{ marginBottom: '1rem', fontSize: '0.8rem' }}>{cur} · Your business</p>
      {loadingIS ? <p className="text-muted">Loading…</p> : (
        <div className="card" style={{ padding: '1rem 1.1rem' }}>
          <Row label="Receivables (outstanding)" value={fmt(summary?.receivables)} bold color="var(--color-warning)" />
          <Row label="Stock value (at cost)"     value={fmt(summary?.stock_value)} bold />
        </div>
      )}
    </div>
  );

  // ── Full owner / manager view ─────────────────────────────────────────────
  return (
    <div className="page">
      <BackButton />

      {/* Header row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.6rem' }}>
        <div>
          <h1 className="page-title" style={{ margin: 0 }}>Financials</h1>
          {isManager && (
            <p className="text-muted" style={{ fontSize: '0.78rem', margin: '0.2rem 0 0' }}>
              {business?.name}
            </p>
          )}
        </div>

        {/* Export — owner only */}
        {isOwner && (
          <button
            onClick={handleExport}
            disabled={exporting || !stmts}
            style={{
              flexShrink: 0,
              padding: '0.4rem 0.9rem',
              borderRadius: 8,
              border: '1px solid var(--color-border)',
              background: 'var(--color-surface)',
              color: 'var(--color-muted)',
              fontSize: '0.8rem',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {exporting ? '…' : '⬇ Export CSV'}
          </button>
        )}
      </div>

      {/* Business selector — owner with multiple businesses */}
      {isOwner && businesses.length > 1 && (
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          style={{ width: '100%', marginBottom: '1rem', fontSize: '0.88rem' }}
        >
          {businesses.map(b => (
            <option key={b.id} value={b.id}>{b.name}</option>
          ))}
        </select>
      )}

      {/* Tab bar */}
      <div style={{ display: 'flex', gap: '0.4rem', margin: '0.25rem 0 1.1rem', overflowX: 'auto', paddingBottom: '2px' }}>
        {TABS.map(t => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              flexShrink: 0,
              padding: '0.45rem 0.9rem',
              borderRadius: '999px',
              border: 'none',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              background: tab === t ? 'var(--color-primary)' : 'var(--color-surface)',
              color:      tab === t ? '#fff' : 'var(--color-muted)',
              transition: 'background 0.15s, color 0.15s',
            }}
          >
            {t}
          </button>
        ))}
      </div>

      {/* ── Income Statement ─────────────────────────────────── */}
      {tab === 'Income Statement' && (
        <>
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.75rem' }}>
            <select value={year} onChange={e => { setYear(e.target.value); setMonth(''); }} style={{ flex: 1, fontSize: '0.85rem' }}>
              <option value="">All time</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
            <select value={month} onChange={e => setMonth(e.target.value)} disabled={!year} style={{ flex: 1, fontSize: '0.85rem', opacity: year ? 1 : 0.4 }}>
              <option value="">All months</option>
              {MONTHS.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <p className="text-muted" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>
            {cur} · {periodLabel()}
          </p>

          {loadingIS ? <p className="text-muted">Loading…</p> : (
            <div className="card" style={{ padding: '1rem 1.1rem' }}>
              <Section title="Revenue">
                <Row label="Total Sales Revenue"   value={fmt(summary?.revenue)}       onClick={isOwner ? undefined : () => goToDetail('revenue')} />
              </Section>
              <Section title="Cost of Goods Sold">
                <Row label="Cost of goods sold (at cost when sold)" value={fmt(summary?.cogs)} onClick={isOwner ? undefined : () => goToDetail('cogs')} />
              </Section>
              <Row label="Gross Profit"             value={fmt(summary?.gross_profit)}  bold color={valColor(summary?.gross_profit)} />
              <Divider />
              <Section title="Operating Expenses">
                <Row label="Total Expenses"         value={fmt(summary?.total_expenses)} onClick={isOwner ? undefined : () => goToDetail('expenses')} />
                {Number(summary?.stock_adjustments) !== 0 && (
                  <Row label="Stock adjustments (count corrections, write-offs)" value={fmt(summary?.stock_adjustments)} color={valColor(summary?.stock_adjustments)} />
                )}
              </Section>
              <Row label="Net Profit / (Loss)"      value={fmt(summary?.net_profit)}    bold color={valColor(summary?.net_profit)} />
              <Divider />
              <Section title="Other">
                <Row label="Cash received in this period" value={fmt(summary?.cash_received)} />
                <Row label="Receivables (outstanding)" value={fmt(summary?.receivables)} onClick={isOwner ? undefined : () => goToDetail('receivables')} />
                <Row label="Stock value (at cost)"     value={fmt(summary?.stock_value)} onClick={isOwner ? undefined : () => goToDetail('stock')} />
              </Section>
            </div>
          )}
        </>
      )}

      {/* ── Balance Sheet ─────────────────────────────────────── */}
      {tab === 'Balance Sheet' && (
        <>
          <p className="text-muted" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>{cur} · As of today</p>
          {loadingST ? <p className="text-muted">Loading…</p> : !stmts ? null : (
            <div className="card" style={{ padding: '1rem 1.1rem' }}>
              <Section title="Assets">
                <div style={{ fontSize: '0.74rem', fontWeight: 600, color: 'var(--color-muted)', marginBottom: '0.2rem' }}>Current Assets</div>
                <Row label="Cash"                       value={fmt(stmts.balance_sheet.assets.current.cash)}        indent color={valColor(stmts.balance_sheet.assets.current.cash)} />
                <Row label="Receivables (outstanding)"  value={fmt(stmts.balance_sheet.assets.current.receivables)} indent />
                <Row label="Stock (at cost)"            value={fmt(stmts.balance_sheet.assets.current.stock_value)} indent />
                {Number(stmts.balance_sheet.assets.current.cash) < 0 && (
                  <p style={{ fontSize: '0.72rem', color: 'var(--color-danger)', margin: '0.1rem 0 0.3rem 1rem' }}>
                    More has been spent than has come in (including capital) — record any missing capital or sales.
                  </p>
                )}
                <Row label="Total Current Assets"       value={fmt(stmts.balance_sheet.assets.current.total)}       bold />
                <Divider />
                <div style={{ fontSize: '0.74rem', fontWeight: 600, color: 'var(--color-muted)', margin: '0.5rem 0 0.2rem' }}>Fixed / Non-Current Assets</div>
                {stmts.balance_sheet.assets.fixed.items.length === 0
                  ? <p style={{ fontSize: '0.8rem', color: 'var(--color-muted)', margin: '0.2rem 0 0.5rem 1rem' }}>No assets recorded yet.</p>
                  : stmts.balance_sheet.assets.fixed.items.map(a => (
                      <Row key={a.id} label={a.name} value={fmt(a.value)} indent />
                    ))
                }
                <Row label="Total Fixed Assets" value={fmt(stmts.balance_sheet.assets.fixed.total)} bold />
                <Divider />
                <Row label="TOTAL ASSETS" value={fmt(stmts.balance_sheet.assets.total)} bold />
              </Section>

              <Section title="Liabilities">
                {Number(stmts.balance_sheet.liabilities.customer_credit) > 0 && (
                  <Row label="Customer overpayments (owed back)" value={fmt(stmts.balance_sheet.liabilities.customer_credit)} indent />
                )}
                <Row label="Total Liabilities" value={fmt(stmts.balance_sheet.liabilities.total)} />
                <p style={{ fontSize: '0.72rem', color: 'var(--color-muted)', margin: '0.1rem 0 0' }}>Purchases are treated as paid in cash — supplier credit isn't tracked yet.</p>
              </Section>

              <Section title="Equity">
                <Row label="Owner's Capital (from company)"  value={fmt(stmts.balance_sheet.equity.owner_capital)}     indent />
                <Row label="Retained Earnings (Net Profit)"  value={fmt(stmts.balance_sheet.equity.retained_earnings)} indent color={valColor(stmts.balance_sheet.equity.retained_earnings)} />
                <Row label="TOTAL EQUITY"                    value={fmt(stmts.balance_sheet.equity.total)}             bold color={valColor(stmts.balance_sheet.equity.total)} />
              </Section>

              <div style={{ marginTop: '0.75rem', padding: '0.55rem 0.7rem', borderRadius: '8px',
                background: 'rgba(255,255,255,0.04)', fontSize: '0.73rem', color: 'var(--color-muted)', textAlign: 'center' }}>
                Assets&nbsp;({fmt(stmts.balance_sheet.assets.total)}) = Liabilities&nbsp;({fmt(stmts.balance_sheet.liabilities.total)}) + Equity&nbsp;({fmt(stmts.balance_sheet.equity.total)})
                {stmts.balance_sheet.balanced ? ' ✓' : ''}
              </div>
            </div>
          )}
        </>
      )}

      {/* ── Cash Flow ─────────────────────────────────────────── */}
      {tab === 'Cash Flow' && (
        <>
          <p className="text-muted" style={{ marginBottom: '0.75rem', fontSize: '0.8rem' }}>{cur} · All time</p>
          {loadingST ? <p className="text-muted">Loading…</p> : !stmts ? null : (
            <div className="card" style={{ padding: '1rem 1.1rem' }}>
              <Section title="Operating Activities">
                <Row label="Cash received from customers"      value={fmt(stmts.cash_flow.operating.cash_from_customers)}   indent />
                <Row label="Payments to suppliers (purchases)" value={fmt(stmts.cash_flow.operating.payments_to_suppliers)} indent />
                <Row label="Payments for operating expenses"   value={fmt(stmts.cash_flow.operating.payments_for_expenses)} indent />
                <Row label="Net Cash from Operations"          value={fmt(stmts.cash_flow.operating.net)} bold color={valColor(stmts.cash_flow.operating.net)} />
              </Section>
              <Section title="Investing Activities">
                <Row label="Capital asset purchases" value={fmt(stmts.cash_flow.investing.asset_purchases)} indent />
                <Row label="Net Cash from Investing" value={fmt(stmts.cash_flow.investing.net)} bold color={valColor(stmts.cash_flow.investing.net)} />
              </Section>
              {stmts.cash_flow.financing && (
                <Section title="Financing Activities">
                  <Row label="Initial capital contributed" value={fmt(stmts.cash_flow.financing.initial_capital)}     indent />
                  <Row label="Capital injections"          value={fmt(stmts.cash_flow.financing.capital_injections)}  indent />
                  {stmts.cash_flow.financing.capital_withdrawals !== 0 && (
                    <Row label="Capital withdrawals" value={fmt(stmts.cash_flow.financing.capital_withdrawals)} indent color="var(--color-danger)" />
                  )}
                  <Row label="Net Cash from Financing" value={fmt(stmts.cash_flow.financing.net)} bold color={valColor(stmts.cash_flow.financing.net)} />
                </Section>
              )}
              <Divider />
              <Row label="NET CHANGE IN CASH" value={fmt(stmts.cash_flow.net_change)} bold color={valColor(stmts.cash_flow.net_change)} />
            </div>
          )}
        </>
      )}
    </div>
  );
}
